import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Column, useTable } from 'react-table';
import { saveAs } from 'file-saver';

import {
  Anchor,
  Button,
  SkeletonLoader,
  Table,
} from '@chargify/custom-ui-components';
import { DataAccessContext } from '@chargify/data-access';

import {
  IconWIthTooltip,
  PagesPagination,
  SkeletonWrapper,
  Status,
} from './components';
import { ConfigContext } from '../../contexts';
import { getHiddenColumns } from './utils';
import { InvoiceTableData } from './types';
import { PaginationVariant } from '../../types';

import FiltersContainer from '../../containers/FiltersContainer';
import { NoInvoicesMsg } from './components/NoInvoicesMsg';
import { SortDir } from '../../models/billingHistory/billingHistory';

export interface InvoicesTableProps {
  invoices: InvoiceTableData[];
  onSortChange: (id: string) => void;
  sortMode: {
    sortBy: string;
    sortDirection: SortDir;
  };
  onPageChange: (page: number) => void;
  onLoadMore: () => void;
  page: number;
  totalPages?: number;
  totalInvoices?: number;
  paginationVariant?: PaginationVariant;
  itemsPerPage: number;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  isLoading: boolean;
  isFiltering: boolean;
}

const InvoicesTable = ({
  invoices,
  onSortChange,
  sortMode,
  onPageChange,
  page,
  onLoadMore,
  totalPages,
  totalInvoices,
  paginationVariant = 'simple',
  itemsPerPage,
  onItemsPerPageChange,
  isLoading,
  isFiltering,
}: InvoicesTableProps) => {
  const {
    billingHistory: { visibleColumns, enableFiltering },
  } = useContext(ConfigContext);
  const { dataAccess } = useContext(DataAccessContext);
  const [saveInvoiceLoading, setSaveInvoiceLoading] = useState(null);

  const { t } = useTranslation('billing-history');
  const navigate = useNavigate();

  const renderFooter = () => {
    switch (paginationVariant) {
      case 'simple':
        if (invoices.length === totalInvoices) return null;
        return (
          <Table.Footer justifyContent={'center'} paddingY={4}>
            <Button
              label={t('show_older_invoices')}
              color={'secondary'}
              onClick={onLoadMore}
            />
          </Table.Footer>
        );
      case 'pages':
        return (
          <PagesPagination
            totalPages={totalPages}
            totalInvoices={totalInvoices}
            page={page}
            onPageChange={onPageChange}
            onItemsPerPageChange={onItemsPerPageChange}
            itemsPerPage={itemsPerPage}
          />
        );
    }
  };

  const getHeaderProps = (column: Column<InvoiceTableData>) => {
    return {
      onClick: () => {
        onSortChange(column.id);
      },
      sortMode: sortMode.sortBy === column.id ? sortMode.sortDirection : null,
      align: column.align,
      minWidth: 160,
    };
  };

  const columns = React.useMemo<Column<InvoiceTableData>[]>(
    () => [
      {
        Header: ({ column }) => (
          <Table.Header {...getHeaderProps(column)}>
            {t('invoice')}
          </Table.Header>
        ),
        id: 'invoiceNumber',
        accessor: 'number',
        Cell: ({ cell, row }) => (
          <Table.Cell align={cell.column.align} gridGap={2} minWidth={160}>
            <IconWIthTooltip
              consolidationLevel={row.original.consolidationLevel}
            />
            <Anchor label={cell.value} />
          </Table.Cell>
        ),
      },
      {
        Header: ({ column }) => (
          <Table.Header {...getHeaderProps(column)}>
            {t('issue_date')}
          </Table.Header>
        ),
        Cell: ({ cell }) => (
          <Table.Cell align={cell.column.align}>{cell.value}</Table.Cell>
        ),
        accessor: 'issueDate',
      },
      {
        Header: ({ column }) => (
          <Table.Header {...getHeaderProps(column)}>{t('status')}</Table.Header>
        ),
        Cell: ({ cell }) => (
          <Table.Cell align={cell.column.align}>
            <Status status={cell.value} />
          </Table.Cell>
        ),
        align: 'center',
        accessor: 'status',
      },
      {
        Header: ({ column }) => (
          <Table.Header {...getHeaderProps(column)}>
            {t('total_amount')}
          </Table.Header>
        ),
        accessor: 'totalAmount',
        Cell: ({ cell }) => (
          <Table.Cell align={cell.column.align}>{cell.value}</Table.Cell>
        ),
        align: 'right',
      },
      {
        Header: ({ column }) => (
          <Table.Header {...getHeaderProps(column)}>
            {t('amount_due')}
          </Table.Header>
        ),
        Cell: ({ cell }) => (
          <Table.Cell align={cell.column.align}>{cell.value}</Table.Cell>
        ),
        accessor: 'amountDue',
        align: 'right',
      },

      {
        Header: ({ column }) => (
          <Table.Header
            {...getHeaderProps(column)}
            paddingY={5}
            disableSort
            data-testid={'table-filter'}
          >
            {enableFiltering && <FiltersContainer />}
          </Table.Header>
        ),
        Cell: ({ cell }) => (
          <Table.Cell align={cell.column.align}>
            <Button
              label={'PDF'}
              iconLeft={'arrowDown'}
              size={'small'}
              isLoading={saveInvoiceLoading === cell.value}
              onClick={(e) => {
                e.stopPropagation();
                setSaveInvoiceLoading(cell.value);
                dataAccess
                  .getInvoicePDF(cell.value)
                  .then((blob) => {
                    const fileURL = URL.createObjectURL(
                      new Blob([blob], { type: 'application/pdf' })
                    );
                    saveAs(fileURL, cell.value);
                  })
                  .finally(() => setSaveInvoiceLoading(null));
              }}
            />
          </Table.Cell>
        ),
        accessor: 'id',
        align: 'right',
      },
    ],
    [sortMode, saveInvoiceLoading]
  );

  const { headerGroups, rows, prepareRow } = useTable({
    data: invoices,
    columns,
    initialState: {
      hiddenColumns: [...getHiddenColumns(visibleColumns)],
    },
  });

  return (
    <Table renderFooter={renderFooter}>
      <Table.Head variant={'primary'}>
        {headerGroups.map((headerGroup, i) => (
          <Table.Row disableBorder variant={'head'} key={headerGroup.id + i}>
            {headerGroup.headers.map((column) => (
              <Table.CellContainer as={'th'} key={column.id}>
                {column.render('Header')}
              </Table.CellContainer>
            ))}
          </Table.Row>
        ))}
      </Table.Head>
      <Table.Body>
        {!isLoading &&
          rows.map((row) => {
            prepareRow(row);
            return (
              <Table.Row
                key={row.original.number}
                onClick={() => navigate(`invoices/${row.original.id}`)}
              >
                {row.cells.map((cell, index) => (
                  <Table.CellContainer key={cell.value + index}>
                    {cell.render('Cell')}
                  </Table.CellContainer>
                ))}
              </Table.Row>
            );
          })}
        {isLoading &&
          Array.from(Array(itemsPerPage).keys()).map((k) => (
            <Table.Row key={k}>
              <SkeletonWrapper>
                <SkeletonLoader height={28} />
              </SkeletonWrapper>
            </Table.Row>
          ))}
        {isFiltering && !isLoading && !invoices.length && (
          <NoInvoicesMsg message={t('no_invoice_matching_filters')} />
        )}
      </Table.Body>
    </Table>
  );
};

export default InvoicesTable;
